import { replace } from 'connected-react-router'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { UpdateThingToDoItemViewModel } from './models'
import {
  extractEditableEndDateTimeStrings,
  extractEditableStartDateTimeStrings,
  isEndDateSame,
  isEndPlaceSame,
  updateThingToDoPayloadFromViewModel,
} from './utils'
import { formOptions } from '../Common/Fields/config'
import ThingToDoFields from '../Common/ThingToDoFields'
import DeleteTripItem from '../DeleteTripItem'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineTrashAltIcon from 'components/Luxkit/Icons/line/LineTrashAltIcon'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useIsMobileScreen } from 'lib/deviceUtils'
import { useUpdateTripItem } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ThingToDoItem, TripItem } from 'tripPlanner/types/tripItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

const itemToView = (item: ThingToDoItem): UpdateThingToDoItemViewModel => {
  const {
    id,
    tripId,
    createdAt,
    updatedAt,
    userUpdatedAt,
    createdBy,
    updatedBy,
    startUtcDateTime,
    endUtcDateTime,
    sortableName,
    ...editableFields
  } = item

  return {
    ...editableFields,
    ...extractEditableStartDateTimeStrings(item),
    ...extractEditableEndDateTimeStrings(item),
    isEndDateSame: isEndDateSame(item),
    isEndPlaceSame: isEndPlaceSame(item),
  }
}

interface Props {
  item: ThingToDoItem
}

const EditThingToDo = (props: Props) => {
  const { item } = props
  const dispatch = useAppDispatch()

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<UpdateThingToDoItemViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })
  const { handleSubmit } = methods

  const isMobileScreen = useIsMobileScreen()

  const { mutate: updateMutate, isLoading } = useUpdateTripItem({
    onSuccess: ({ id }: TripItem) => {
      dispatch(replace({ hash: itemSectionId(id) }))
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateAttractionItem = useCallback(
    ({ ...viewModel }: UpdateThingToDoItemViewModel) => {
      const data = updateThingToDoPayloadFromViewModel(viewModel)

      updateMutate({
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: data,
      })
    },
    [item.id, item.tripId, updateMutate],
  )

  const onSubmit = useMemo(
    () => handleSubmit(updateAttractionItem),
    [handleSubmit, updateAttractionItem],
  )

  const trashIcon = useMemo(() => <LineTrashAltIcon />, [])

  return (
    <>
      <ModalHeader title="Edit thing to do" onCloseButtonClick={closeModal} />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <ThingToDoFields tripId={item.tripId} />
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Save',
          disabled: isLoading,
          onClick: onSubmit,
        }}
        secondaryActionProps={{
          children: 'Delete',
          disabled: isLoading,
          onClick: deleteItem,
          fit: isMobileScreen ? 'mobile-full-width' : 'auto',
          startIcon: trashIcon,
        }}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditThingToDo
